import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import {
  getMessageRecipients,
  sendMessage,
  getConversations,
  getMessageThread,
  getUnreadMessageCount
} from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  MessageSquare, 
  Send, 
  Users, 
  User, 
  Plus, 
  ArrowLeft,
  Loader2,
  Clock,
  CheckCheck,
  RefreshCw
} from 'lucide-react';

export default function Messages() {
  const { user, isAdminOrOps } = useAuth();
  const { locations } = useApp();
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [conversations, setConversations] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [threadMessages, setThreadMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [showNewMessageModal, setShowNewMessageModal] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [newMessageData, setNewMessageData] = useState({
    recipient_id: '',
    message_type: 'DIRECT',
    content: ''
  });
  const messagesEndRef = useRef(null);
  
  const userIsAdminOrOps = isAdminOrOps();
  const userLocations = user?.assigned_location_ids || [];
  
  // Get available locations for user
  const availableLocations = userIsAdminOrOps 
    ? locations.filter(l => l.is_active)
    : locations.filter(l => l.is_active && userLocations.includes(l.id));

  // Check if user is employee only
  const isEmployeeOnly = user?.roles?.includes('EMPLOYEE') && 
    !user?.roles?.some(r => ['ADMIN', 'OPS_COORDINATOR', 'MANAGER', 'ASSISTANT_MANAGER'].includes(r));

  const loadConversations = useCallback(async () => {
    if (!selectedLocation) return;
    
    setLoading(true);
    try {
      const res = await getConversations(selectedLocation);
      setConversations(res.data);
    } catch (error) {
      console.error('Failed to load conversations:', error);
      toast.error('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  }, [selectedLocation]);

  const loadThreadMessages = useCallback(async (threadId) => {
    if (!selectedLocation || !threadId) return;
    
    try {
      const res = await getMessageThread(threadId, selectedLocation);
      setThreadMessages(res.data);
      // Scroll to bottom after loading
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } catch (error) {
      console.error('Failed to load thread:', error);
      toast.error('Failed to load messages');
    }
  }, [selectedLocation]);

  const loadRecipients = useCallback(async () => {
    if (!selectedLocation) return;
    
    try {
      const res = await getMessageRecipients(selectedLocation);
      setRecipients(res.data);
    } catch (error) {
      console.error('Failed to load recipients:', error);
    }
  }, [selectedLocation]);

  useEffect(() => {
    // Auto-select first location if only one available
    if (availableLocations.length === 1 && !selectedLocation) {
      setSelectedLocation(availableLocations[0].id);
    }
  }, [availableLocations, selectedLocation]);

  useEffect(() => {
    if (selectedLocation) {
      loadConversations();
      loadRecipients();
    }
  }, [selectedLocation, loadConversations, loadRecipients]);

  useEffect(() => {
    if (selectedThread) {
      loadThreadMessages(selectedThread.id);
      // Poll for new messages every 10 seconds
      const interval = setInterval(() => {
        loadThreadMessages(selectedThread.id);
      }, 10000);
      return () => clearInterval(interval);
    }
  }, [selectedThread, loadThreadMessages]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedThread || !selectedLocation) return;
    
    setSending(true);
    try {
      const messageData = {
        location_id: selectedLocation,
        content: newMessage.trim(),
        message_type: selectedThread.type === 'group' ? 'GROUP' : 'DIRECT',
        recipient_id: selectedThread.type === 'direct' ? selectedThread.other_user_id : null
      };
      
      await sendMessage(messageData);
      setNewMessage('');
      loadThreadMessages(selectedThread.id);
      loadConversations(); // Refresh conversation list
      toast.success('Message sent');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleNewMessage = async () => {
    if (!newMessageData.content.trim() || !selectedLocation) {
      toast.error('Please enter a message');
      return;
    }
    
    if (newMessageData.message_type === 'DIRECT' && !newMessageData.recipient_id) {
      toast.error('Please select a recipient');
      return;
    }
    
    setSending(true);
    try {
      await sendMessage({
        location_id: selectedLocation,
        content: newMessageData.content.trim(),
        message_type: newMessageData.message_type,
        recipient_id: newMessageData.message_type === 'DIRECT' ? newMessageData.recipient_id : null
      });
      
      setShowNewMessageModal(false);
      setNewMessageData({ recipient_id: '', message_type: 'DIRECT', content: '' });
      loadConversations();
      toast.success('Message sent');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  const getRoleBadge = (roles) => {
    if (!roles || roles.length === 0) return null;
    if (roles.includes('ADMIN')) return <Badge variant="destructive" className="text-xs">Admin</Badge>;
    if (roles.includes('OPS_COORDINATOR')) return <Badge variant="secondary" className="text-xs">Ops</Badge>;
    if (roles.includes('MANAGER')) return <Badge className="text-xs bg-blue-500">Manager</Badge>;
    if (roles.includes('ASSISTANT_MANAGER')) return <Badge className="text-xs bg-purple-500">Asst Mgr</Badge>;
    if (roles.includes('EMPLOYEE')) return <Badge variant="outline" className="text-xs">Employee</Badge>;
    return null;
  };

  // Filter recipients based on user role
  const filteredRecipients = recipients.filter(r => {
    if (isEmployeeOnly) {
      // Employees can only message managers and assistant managers
      return r.roles?.some(role => ['MANAGER', 'ASSISTANT_MANAGER'].includes(role));
    }
    return true; // Managers+ can see all recipients
  });

  return (
    <div className="h-[calc(100vh-120px)] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <MessageSquare className="h-6 w-6 text-blue-600" />
          <h1 className="text-2xl font-bold">Messages</h1>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Location Selector */}
          {availableLocations.length > 1 && (
            <Select value={selectedLocation} onValueChange={setSelectedLocation}>
              <SelectTrigger className="w-[200px]" data-testid="location-select">
                <SelectValue placeholder="Select Location" />
              </SelectTrigger>
              <SelectContent>
                {availableLocations.map(loc => (
                  <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          
          <Button 
            onClick={() => setShowNewMessageModal(true)}
            disabled={!selectedLocation}
            data-testid="new-message-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Message
          </Button>
        </div>
      </div>

      {!selectedLocation ? (
        <Card className="flex-1 flex items-center justify-center">
          <div className="text-center text-gray-500">
            <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>Select a location to view messages</p>
          </div>
        </Card>
      ) : (
        <div className="flex-1 flex gap-4 min-h-0">
          {/* Conversations List */}
          <Card className={`${selectedThread ? 'hidden md:block' : ''} w-full md:w-80 flex flex-col`}>
            <CardHeader className="py-3 px-4 border-b">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium">Conversations</CardTitle>
                <Button variant="ghost" size="icon" onClick={loadConversations}>
                  <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                </Button>
              </div>
            </CardHeader>
            <ScrollArea className="flex-1">
              {loading && conversations.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
                  Loading...
                </div>
              ) : conversations.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No conversations yet</p>
                  <p className="text-xs mt-1">Start a new message!</p>
                </div>
              ) : (
                <div className="divide-y">
                  {conversations.map(conv => (
                    <div
                      key={conv.id}
                      className={`p-3 cursor-pointer hover:bg-gray-50 transition-colors ${
                        selectedThread?.id === conv.id ? 'bg-blue-50 border-l-2 border-blue-500' : ''
                      }`}
                      onClick={() => setSelectedThread(conv)}
                      data-testid={`conversation-${conv.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-full ${conv.type === 'group' ? 'bg-green-100' : 'bg-gray-100'}`}>
                          {conv.type === 'group' ? (
                            <Users className="h-4 w-4 text-green-600" />
                          ) : (
                            <User className="h-4 w-4 text-gray-600" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-medium text-sm truncate">{conv.name}</span>
                            {conv.unread_count > 0 && (
                              <Badge className="bg-blue-500 text-xs">{conv.unread_count}</Badge>
                            )}
                          </div>
                          {conv.roles && <div className="mt-0.5">{getRoleBadge(conv.roles)}</div>}
                          <p className="text-xs text-gray-500 truncate mt-1">
                            {conv.type === 'group' && conv.last_message_sender && (
                              <span className="font-medium">{conv.last_message_sender}: </span>
                            )}
                            {conv.last_message}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">{formatTime(conv.last_message_time)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </Card>

          {/* Message Thread */}
          <Card className={`flex-1 flex flex-col ${!selectedThread ? 'hidden md:flex' : ''}`}>
            {selectedThread ? (
              <>
                {/* Thread Header */}
                <CardHeader className="py-3 px-4 border-b">
                  <div className="flex items-center gap-3">
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="md:hidden"
                      onClick={() => setSelectedThread(null)}
                    >
                      <ArrowLeft className="h-4 w-4" />
                    </Button>
                    <div className={`p-2 rounded-full ${selectedThread.type === 'group' ? 'bg-green-100' : 'bg-gray-100'}`}>
                      {selectedThread.type === 'group' ? (
                        <Users className="h-5 w-5 text-green-600" />
                      ) : (
                        <User className="h-5 w-5 text-gray-600" />
                      )}
                    </div>
                    <div>
                      <CardTitle className="text-base">{selectedThread.name}</CardTitle>
                      {selectedThread.roles && (
                        <div className="mt-1">{getRoleBadge(selectedThread.roles)}</div>
                      )}
                    </div>
                  </div>
                </CardHeader>

                {/* Messages */}
                <ScrollArea className="flex-1 p-4">
                  <div className="space-y-4">
                    {threadMessages.map(msg => (
                      <div
                        key={msg.id}
                        className={`flex ${msg.is_own ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[75%] rounded-lg px-4 py-2 ${
                            msg.is_own
                              ? 'bg-blue-500 text-white'
                              : 'bg-gray-100 text-gray-900'
                          }`}
                        >
                          {!msg.is_own && selectedThread.type === 'group' && (
                            <p className="text-xs font-medium mb-1 opacity-75">
                              {msg.sender_name}
                            </p>
                          )}
                          <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                          <div className={`flex items-center gap-1 mt-1 text-xs ${
                            msg.is_own ? 'text-blue-100' : 'text-gray-400'
                          }`}>
                            <Clock className="h-3 w-3" />
                            {formatTime(msg.created_at)}
                            {msg.is_own && msg.is_read && (
                              <CheckCheck className="h-3 w-3 ml-1" />
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>

                {/* Message Input */}
                <div className="p-4 border-t">
                  <div className="flex gap-2">
                    <Input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type a message..."
                      onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                      disabled={sending}
                      data-testid="message-input"
                    />
                    <Button 
                      onClick={handleSendMessage}
                      disabled={sending || !newMessage.trim()}
                      data-testid="send-message-btn"
                    >
                      {sending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>Select a conversation to view messages</p>
                  <p className="text-sm mt-1">or start a new conversation</p>
                </div>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* New Message Modal */}
      <Dialog open={showNewMessageModal} onOpenChange={setShowNewMessageModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>New Message</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Message Type */}
            <div>
              <Label>Message Type</Label>
              <Select 
                value={newMessageData.message_type} 
                onValueChange={(v) => setNewMessageData({...newMessageData, message_type: v, recipient_id: ''})}
              >
                <SelectTrigger data-testid="message-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="DIRECT">Direct Message</SelectItem>
                  {!isEmployeeOnly && (
                    <SelectItem value="GROUP">Location Group Chat</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Recipient (for direct messages) */}
            {newMessageData.message_type === 'DIRECT' && (
              <div>
                <Label>Send To</Label>
                <Select 
                  value={newMessageData.recipient_id} 
                  onValueChange={(v) => setNewMessageData({...newMessageData, recipient_id: v})}
                >
                  <SelectTrigger data-testid="recipient-select">
                    <SelectValue placeholder="Select recipient" />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredRecipients.map(r => (
                      <SelectItem key={r.id} value={r.id}>
                        <div className="flex items-center gap-2">
                          <span>{r.name}</span>
                          {getRoleBadge(r.roles)}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {isEmployeeOnly && (
                  <p className="text-xs text-gray-500 mt-1">
                    You can only message Managers and Assistant Managers at your location.
                  </p>
                )}
              </div>
            )}

            {/* Location Info for Group */}
            {newMessageData.message_type === 'GROUP' && (
              <div className="p-3 bg-green-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-green-600" />
                  <span className="text-sm font-medium text-green-800">
                    Message will be sent to all users at {availableLocations.find(l => l.id === selectedLocation)?.name}
                  </span>
                </div>
              </div>
            )}

            {/* Message Content */}
            <div>
              <Label>Message</Label>
              <Textarea
                value={newMessageData.content}
                onChange={(e) => setNewMessageData({...newMessageData, content: e.target.value})}
                placeholder="Type your message..."
                rows={4}
                data-testid="new-message-content"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewMessageModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleNewMessage}
              disabled={sending || !newMessageData.content.trim()}
              data-testid="send-new-message-btn"
            >
              {sending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Send Message
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
